import { OnDatalayerProps } from '@/features/shared/HOCs/withGTM.interface';
import React, { RefObject, useCallback, useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { datalayerPush } from '@/features/shared/HOCs/withGTM';

export const onDatalayerCustomSubmit = ({ target }: OnDatalayerProps) => {
  if (!target) {
    return undefined;
  }

  const { variantName = '', ...props } = target;

  return {
    eventCategory: 'Forms',
    ...props,
    ...(!!variantName && { variant_name: variantName }),
  };
};

export const useMarketoFormDatalayerPosition = (containerRef?: RefObject<HTMLDivElement>) => {
  const getDatalayerPosition = useCallback(() => {
    return (
      containerRef?.current
        ?.closest('[data-datalayer-position]')
        ?.getAttribute('data-datalayer-position') || ''
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { getDatalayerPosition };
};

export const useMarketoFormDatalayer = (
  formId?: string,
  setParentDataLayerFormAttributes?: React.Dispatch<
    React.SetStateAction<{
      lastActiveFieldName: string;
      formType: string;
      formName: string;
    }>
  >,
  formContainerRef?: RefObject<HTMLDivElement>,
) => {
  const [isTriggeredDataLayerFocus, setIsTriggeredDataLayerFocus] = React.useState(false);
  const [activeDataLayerFieldNames, setActiveDataLayerFieldNames] = React.useState<string[]>([]);
  const { getDatalayerPosition } = useMarketoFormDatalayerPosition(formContainerRef);

  const dataLayerFocusEvent = React.useCallback(
    (fieldName = '', formValues) => {
      const position = getDatalayerPosition();

      const dataLayerBase = {
        ...(!!formValues?.formName && { form_name: formValues?.formName }),
        ...(!!formValues?.formType && { form_type: formValues?.formType }),
        ...(!!position && { position }),
        form_id: formId,
        ...(!!fieldName && { form_field: fieldName }),
      };

      if (!activeDataLayerFieldNames.includes(fieldName)) {
        TagManager.dataLayer({
          dataLayer: {
            event: 'trackEvent-formFieldActive',
            event_name: 'form_field_active',
            ...dataLayerBase,
          },
        });

        if (setParentDataLayerFormAttributes)
          setParentDataLayerFormAttributes((prev) => ({
            ...prev,
            lastActiveFieldName: fieldName,
          }));
        setActiveDataLayerFieldNames((prev) => [...prev, fieldName]);
      }

      if (!isTriggeredDataLayerFocus) {
        TagManager.dataLayer({
          dataLayer: {
            event: 'trackEvent-formStart',
            event_name: 'form_start',
            ...dataLayerBase,
          },
        });
        setIsTriggeredDataLayerFocus(true);
      }
    },
    [
      activeDataLayerFieldNames,
      formId,
      isTriggeredDataLayerFocus,
      setParentDataLayerFormAttributes,
      getDatalayerPosition,
    ],
  );

  const datalayerFormErrorEvent = useCallback(
    (formValues: any, errorControl: any) => {
      const position = getDatalayerPosition();

      const dataLayer = {
        event: 'trackEvent-formError',
        event_name: 'form_error',
        ...(!!formValues?.formName && { form_name: formValues?.formName }),
        ...(!!formValues?.formType && { form_type: formValues?.formType }),
        form_id: formId,
        ...(!!position && { position }),
        ...(!!errorControl?.attributes?.name && { form_field: errorControl?.attributes?.name }),
        ...(!!errorControl?.error && { error_message: errorControl?.error }),
      };

      TagManager.dataLayer({ dataLayer });
    },
    [formId, getDatalayerPosition],
  );

  const datalayerClickToFormEvent = useCallback(
    (
      formValues: Record<string, any>,
      isOpenedByCTAVariant?: 'link' | 'button',
      datalayerLinkText?: string,
    ) => {
      const position = getDatalayerPosition();
      const dataLayerType =
        isOpenedByCTAVariant === 'button' ? 'button_cta_popup_form' : 'link_cta_popup_form';

      const dataLayer = {
        event: 'trackEvent-ctaClickToForm',
        event_name: 'cta_click_to_form',
        ...(!!position && { position }),
        type: dataLayerType,
        link_text: datalayerLinkText,
        ...(!!formValues?.formName && { form_name: formValues?.formName }),
        ...(!!formValues?.formType && { form_type: formValues?.formType }),
        form_id: formId,
      };

      TagManager.dataLayer({ dataLayer });
    },
    [formId, getDatalayerPosition],
  );

  const datalayerFormEngagementEvent = useCallback(
    (
      containerRef: RefObject<HTMLDivElement>,
      formValues: Record<string, any>,
      variantName?: string,
    ) => {
      const position = getDatalayerPosition();

      datalayerPush({
        compRef: containerRef,
        event: 'gtm:custom:form:submit',
        args: {
          event: 'trackEvent-formEngagement',
          event_name: 'form_successful_submission',
          eventCategory: 'Forms',
          eventAction: 'Submission',
          form_id: formId,
          variantName,
          ...(!!position && { position }),
          ...formValues,
        },
      });
    },
    [formId, getDatalayerPosition],
  );

  return {
    dataLayerFocusEvent,
    datalayerFormErrorEvent,
    datalayerClickToFormEvent,
    datalayerFormEngagementEvent,
  };
};
